import type { RecurrenceStreamType } from '@frontend/api-types';

/**
 * Temporary feature flags - experiments, rollouts, short-lived toggles.
 * These should be audited regularly and removed when no longer needed.
 * Use `useTempFeatureFlag` hook to consume these flags.
 */
export type TemporaryFlags = {
  block_alacarte_signup_during_onboarding: boolean;
  is_in_task_search_enabled: boolean;
  should_show_task_statistics: boolean;
};

/**
 * Valid pricing modes for the platform.
 * - 'credit': Traditional credits-based display (default)
 * - 'bandwidth': Bandwidth-based display (hides credits)
 * - 'custom': Custom config from GrowthBook for flexibility
 */
export const PRICING_MODES = ['credit', 'bandwidth', 'custom'] as const;

/**
 * Pricing mode determines how the platform displays pricing/credits terminology.
 */
export type PricingMode = (typeof PRICING_MODES)[number];

/**
 * Single feature flag for First Task Fast-Track: who gets redirected to first task after paywall/onboarding.
 * - 'none': no one (experiment off)
 * - 'all': every member who just completed paywall/onboarding
 * - 'promotion': only members whose applied promotion entity ID is in promotion_ids
 */
export type FirstTaskFastTrackConfig = {
  mode: 'all' | 'none' | 'promotion';
  promotion_ids?: string[];
};

/**
 * Terminology config shape used by all pricing modes.
 */
export type TerminologyConfig = {
  add_button: string;
  balance_title: string;
  cost_label: string;
  expiration_label: string;
  hide_credits: boolean;
  history_title: string;
  insufficient_label: string;
  monthly_progress_direction: 'drain' | 'fill' | null;
  plan_section_subtitle: string;
  plan_subtitle_template: string;
  referral_reward: string;
  unit_term: string;
  unit_term_plural: string;
};

/**
 * Permanent feature flags - configuration, operational controls, long-lived settings.
 * Use `useFeatureIsOn` or `useFeatureValue` hooks to consume these flags.
 */
export type FeatureMap = {
  complexity_bucket_label: string;
  copy_signup_hero: {
    heading: string;
    subheading: string;
  };
  credit_purchase_packages: { credits: number; description: string; original_price_cents: Cent; price_cents: Cent }[];
  default_promotion_code: string;
  experiment_no_payment_signup_credits: number;
  first_task_fast_track: FirstTaskFastTrackConfig;
  free_credits_on_signup: {
    days_until_expiration: number;
    message: string;
    num_credits: number;
    promotion_id_for_variant: string;
    variant_days_until_expiration: number;
    variant_message: string;
    variant_num_credits: number;
  };
  gift_a_task_copy: {
    banner: {
      heading: string;
      label: string;
      subheading: string;
    };
    modal: {
      benefits: string[];
      cta_label: string;
      heading: string;
      subheading: string;
    };
  };
  home_screen_task_title_suggestions: {
    taskSuggestions: string[];
  };
  is_bandwidth_model_enabled: boolean;
  is_discover_tab_enabled: boolean;
  is_gameplan_pricing_enabled: boolean;
  is_home_create_task_input_enabled: boolean;
  is_household_plus_active: boolean;
  is_invisible_pricing_enabled_for_client: boolean;
  is_mobile_sso_sign_in_enabled: boolean;
  is_recurrences_revamped_enabled: boolean;
  is_redirect_linkedin_signups_to_new_task_enabled: boolean;
  is_self_service_plan_mgmt_enabled: boolean;
  is_share_task_enabled: boolean;
  is_signup_calendar_enabled: boolean;
  is_signup_changes_enabled: boolean;
  is_sso_sign_in_enabled: boolean;
  letter_recommendation_active_task_threshold: number;
  linked_in_promotion_entity_id: string;
  maintenance_mode: { expires_at: DateTimeISOString; is_on: boolean; message: string };
  mark_as_done_deploy_date: DateISOString;
  member_cancel_downgrade_copy: {
    button_label: string;
    modal_body: string;
    modal_confirm_button_label: string;
    modal_title: string;
  };
  member_referral_copy: string;
  monthly_bandwidth_progress_direction: 'drain' | 'fill';
  onboarding_url: string;
  pricing_mode: PricingMode;
  pricing_mode_custom_config: Partial<TerminologyConfig>;
  recurrence_stream_settings: Record<RecurrenceStreamType, boolean>;
  should_use_backend_plans: boolean;
  should_use_new_task_renderer: boolean;
  task_done_confirmation: {
    blocking_ui_after_days: number;
    eligible_task_completed_after: string;
    max_blocking_per_day: number;
    max_blocking_tasks_per_event: number;
  };
  unread_letter_threshold: number;
  upgrade_household_cta_copy: {
    benefits: string[];
    button_label: string;
    heading: string;
    subheading: string;
  };
};

/** Combined type for GrowthBook SDK initialization */
export type AllFeatureFlags = FeatureMap & TemporaryFlags;

export type FeatureFlag = keyof AllFeatureFlags;

export const FIRST_TASK_FAST_TRACK_DEFAULT: FeatureMap['first_task_fast_track'] = {
  mode: 'none',
};

export const MEMBER_CANCEL_DOWNGRADE_COPY_DEFAULT_VALUE: FeatureMap['member_cancel_downgrade_copy'] = {
  button_label: 'Downgrade to Pay-As-You-Go',
  modal_body:
    'You will be able to purchase credits as you go and may upgrade to a subscription for discounted credits any time.',
  modal_confirm_button_label: 'Yes, cancel subscription',
  modal_title: 'Downgrade to Pay-As-You-Go?',
};

export const COPY_SIGNUP_HERO_DEFAULT_VALUE: FeatureMap['copy_signup_hero'] = {
  heading: "Life's too short for busywork. Offload the scheduling, shopping, and paperwork — you go do what matters.",
  subheading: 'While Duckbill handles things, you could... Adventure',
};

export const MEMBER_REFERRAL_COPY_DEFAULT_VALUE: FeatureMap['member_referral_copy'] =
  "Share Duckbill with friends! They'll get an exclusive new member offer and you'll earn rewards for each friend who joins.";

export const GIFT_A_TASK_COPY_DEFAULT_VALUE: FeatureMap['gift_a_task_copy'] = {
  banner: {
    heading: 'Send a task to a friend',
    label: 'Gift a Task',
    subheading: 'Hand something off their plate — Duckbill takes care of it.',
  },
  modal: {
    benefits: ['Quick to set up', 'They get a personal invite', 'Send to any friend'],
    cta_label: 'Send Gift',
    heading: 'Hand off something on their plate.',
    subheading: 'A real person handles the task.',
  },
};

export const UPGRADE_HOUSEHOLD_CTA_COPY: FeatureMap['upgrade_household_cta_copy'] = {
  benefits: ['2 accounts with separate logins', 'Each member manages their own tasks'],
  button_label: 'Upgrade to Household',
  heading: 'Share Duckbill with your household',
  subheading:
    'Add a partner, family member, or roommate to your account. Everyone gets their own login and tasks, with shared credits.',
};

export const ECONOMICS_TERMINOLOGY_DEFAULT: TerminologyConfig = {
  add_button: 'Add Credits',
  balance_title: 'Available Credits',
  cost_label: 'Credit cost',
  expiration_label: 'Credits expire monthly',
  hide_credits: false,
  history_title: 'Credit History',
  insufficient_label: 'Buy credits to continue',
  monthly_progress_direction: 'drain',
  plan_section_subtitle: 'Pick your monthly credits pack',
  plan_subtitle_template: '{credits} credits per month',
  referral_reward: "you'll get 15 credits for each friend who joins",
  unit_term: 'credit',
  unit_term_plural: 'credits',
};

/**
 * Predefined pricing mode configurations.
 * - 'credit': Traditional credits-based display
 * - 'bandwidth': Bandwidth-based display (hides credits)
 */
export const PRICING_MODE_CONFIGS: Record<Exclude<PricingMode, 'custom'>, TerminologyConfig> = {
  bandwidth: {
    add_button: 'Add Bandwidth',
    balance_title: 'Monthly Bandwidth',
    cost_label: 'Task size',
    expiration_label: 'Bandwidth expires monthly',
    hide_credits: true,
    history_title: 'Usage',
    insufficient_label: 'Add bandwidth to continue',
    monthly_progress_direction: 'drain',
    plan_section_subtitle: 'Pick your monthly bandwidth',
    plan_subtitle_template: '~{min}-{max} tasks per month',
    referral_reward: "you'll get bonus bandwidth for each friend who joins",
    unit_term: 'bandwidth',
    unit_term_plural: 'bandwidth',
  },
  credit: ECONOMICS_TERMINOLOGY_DEFAULT,
};

export const PRICING_MODE_DEFAULT: PricingMode = 'credit';
